import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast, Toaster } from "react-hot-toast";
import { Search, RefreshCw, Eye, X, Download } from "lucide-react";
import Select from "react-select";
import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import subDays from "date-fns/subDays";
import addDays from "date-fns/addDays";
import startOfWeek from "date-fns/startOfWeek";
import endOfWeek from "date-fns/endOfWeek";
import startOfMonth from "date-fns/startOfMonth";
import endOfMonth from "date-fns/endOfMonth";
import addMonths from "date-fns/addMonths";
import * as XLSX from "xlsx";
import { useCurrentDomain } from "../../context/DomainContext";
import { selectStyles } from "../../helpers";
import {
  getDocumentGenerationServiceLogs,
  getDocumentGenerationApiLogs,
} from "../../services/masterDataService";
import "./DocumentGenerationLogsReport.css";

const predefinedBottomRanges = [
  { label: "Today", value: [new Date(), new Date()] },
  { label: "Yesterday", value: [addDays(new Date(), -1), addDays(new Date(), -1)] },
  { label: "This week", value: [startOfWeek(new Date()), endOfWeek(new Date())] },
  { label: "Last 7 days", value: [subDays(new Date(), 6), new Date()] },
  { label: "Last 30 days", value: [subDays(new Date(), 29), new Date()] },
  { label: "This month", value: [startOfMonth(new Date()), endOfMonth(new Date())] },
  { label: "Last month", value: [addMonths(startOfMonth(new Date()), -1), endOfMonth(addMonths(new Date(), -1))] },
];

const servicePageSizeOptions = [
  { value: 5, label: "5 / page" },
  { value: 15, label: "15 / page" },
  { value: 25, label: "25 / page" },
  { value: 50, label: "50 / page" },
];

const apiPageSizeOptions = [
  { value: 25, label: "25 / page" },
  { value: 50, label: "50 / page" },
  { value: 100, label: "100 / page" },
];

const formatTs = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${mm}/${dd}/${yyyy} ${hh}:${mi}:${ss}`;
};

const daysBetween = (a, b) => Math.round(Math.abs(b.setHours(0, 0, 0, 0) - a.setHours(0, 0, 0, 0)) / 86400000);

const ClampCell = ({ text, title, onOpen }) => {
  const ref = useRef(null);
  const [overflow, setOverflow] = useState(false);
  const value = text || "";

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setOverflow(el.scrollHeight > el.clientHeight + 2);
  }, [value]);

  return (
    <td>
      <div className="dgl-cell">
        <div ref={ref} className="dgl-clamp">{value}</div>
        {overflow && (
          <button type="button" className="dgl-eye" onClick={() => onOpen(title, value)} title="View full text">
            <Eye size={14} />
          </button>
        )}
      </div>
    </td>
  );
};

const DocumentGenerationLogsReport = () => {
  const { domain } = useCurrentDomain();
  const [tab, setTab] = useState("service");
  const [eventId, setEventId] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [applicationName, setApplicationName] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [popup, setPopup] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const [nextContinuationToken, setNextContinuationToken] = useState(null);
  const [prevTokens, setPrevTokens] = useState([]); 

  const pageSizeOptions = tab === "service" ? servicePageSizeOptions : apiPageSizeOptions;

  const totalCount = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePage = Math.min(pageNumber, totalPages);

  const pageRows = useMemo(() => {
    if (tab === "service") return rows;
    const start = (safePage - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, safePage, pageSize, tab]);

  const validateDates = () => {
    if (!dateRange?.[0] || !dateRange?.[1]) return true;
    const from = new Date(dateRange[0]);
    const to = new Date(dateRange[1]);
    const today = new Date();
    const minAllowed = subDays(new Date(), 90);
    if (to < from) {
      toast.error("To date cannot be earlier than From date");
      return false;
    }
    if (daysBetween(new Date(from), new Date(to)) > 90) {
      toast.error("Date range cannot exceed 90 days");
      return false;
    }
    if (tab === "service" && from < minAllowed) {
      toast.error("Service logs are kept for 90 days only");
      return false;
    }
    if (from > today) {
      toast.error("From date cannot be in the future");
      return false;
    }
    return true;
  };

  const buildPayload = (overridePageSize, continuationToken = null) => {
  const toLocalDate = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  };
  const fromDate = toLocalDate(dateRange?.[0]);
  const toDate = toLocalDate(dateRange?.[1]);
    const size = overridePageSize ?? pageSize;
    return {
      domain,
      eventId: eventId.trim(),
      fromDate,
      toDate,
      applicationName: applicationName.trim(),
      maxRows: tab === "service" ? size : 200,
      continuationToken: tab === "service" ? continuationToken : null,
    };
  };

  const fetchServicePage = async (continuationToken, size) => {
    const payload = buildPayload(size, continuationToken);
    const res = await getDocumentGenerationServiceLogs(payload);
    const data = res?.data || res || {};
    setRows(data.rows || []);
    setNextContinuationToken(data.nextContinuationToken || null);
    return data;
  };

  const handleSearch = async () => {
    if (!eventId.trim() && (!dateRange?.[0] || !dateRange?.[1])) {
      toast.error("Enter Event ID or select Date Range");
      return;
    }
    if (!validateDates()) return;

    setLoading(true);
    const started = Date.now();
    try {
      if (tab === "service") {
        setPrevTokens([]);
        setPageNumber(1);
        const data = await fetchServicePage(null, pageSize);
        const ms = Date.now() - started;
        if (!(data.rows || []).length) toast("No logs found");
        else toast.success(`Loaded ${(data.rows || []).length} rows in ${(ms / 1000).toFixed(1)}s`);
      } else {
        const payload = buildPayload();
        const res = await getDocumentGenerationApiLogs(payload);
        const data = res?.data || res || {};
        setRows(data.rows || []);
        setPageNumber(1);
        const ms = Date.now() - started;
        if (!(data.rows || []).length) toast("No logs found");
        else toast.success(`Loaded ${(data.rows || []).length} rows in ${(ms / 1000).toFixed(1)}s`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load logs");
      setRows([]);
      setNextContinuationToken(null);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setEventId("");
    setDateRange(null);
    setApplicationName("");
    setRows([]);
    setPopup(null);
    setPageNumber(1);
    setPageSize(tab === "service" ? 5 : 25);
    setNextContinuationToken(null);
    setPrevTokens([]);
  };

  const switchTab = (next) => {
    if (next === tab) return;
    setTab(next);
    setRows([]);
    setPopup(null);
    setPageNumber(1);
    setPageSize(next === "service" ? 5 : 25);
    setNextContinuationToken(null);
    setPrevTokens([]);
  };

  const handleDownload = () => {
    if (!rows.length) {
      toast.error("No data to download. Search first.");
      return;
    }
    const mapped =
      tab === "service"
        ? rows.map((r) => ({
            PartitionKey: r.partitionKey,
            EventId: r.eventId,
            RowKey: r.rowKey,
            Timestamp: formatTs(r.timestamp),
            AggregatedProperties: r.aggregatedProperties,
            ApplicationName: r.applicationName,
            Level: r.level,
            MachineName: r.machineName,
            RenderedMessage: r.renderedMessage,
            Exception: r.exception,
          }))
        : rows.map((r) => ({
            PartitionKey: r.partitionKey,
            EventId: r.eventId,
            RowKey: r.rowKey,
            Timestamp: formatTs(r.timestamp),
            AggregatedProperties: r.aggregatedProperties,
            ApplicationName: r.applicationName,
            IpAddress: r.ipAddress,
            Level: r.level,
            MachineName: r.machineName,
            RenderedMessage: r.renderedMessage,
            Exception: r.exception,
          }));
    const ws = XLSX.utils.json_to_sheet(mapped);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, tab === "service" ? "ServiceLogs" : "ApiLogs");
    XLSX.writeFile(wb, `DocumentGeneration_${tab === "service" ? "Service" : "API"}_Logs.xlsx`);
  };

  const openPopup = (title, text) => setPopup({ title, text });

  const handlePageSizeChange = async (opt) => {
    const newSize = opt.value;
    setPageSize(newSize);
    setPageNumber(1);

    if (tab !== "service" || !rows.length) return;

    setLoading(true);
    try {
      setPrevTokens([]);
      await fetchServicePage(null, newSize);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load logs");
    } finally {
      setLoading(false);
    }
  };

  const handleServiceNext = async () => {
    if (!nextContinuationToken) return;
    setLoading(true);
    try {
      setPrevTokens((prev) => [...prev, nextContinuationToken]);
      await fetchServicePage(nextContinuationToken, pageSize);
      setPageNumber((p) => p + 1);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load next page");
    } finally {
      setLoading(false);
    }
  };

  const handleServicePrev = async () => {
    if (prevTokens.length === 0) return;
    setLoading(true);
    try {
      const newStack = [...prevTokens];
      newStack.pop(); // discard the token that got us to the current page
      const tokenToUse = newStack.length ? newStack[newStack.length - 1] : null;
      setPrevTokens(newStack);
      await fetchServicePage(tokenToUse, pageSize);
      setPageNumber((p) => Math.max(1, p - 1));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load previous page");
    } finally {
      setLoading(false);
    }
  };

  const canServicePrev = prevTokens.length > 0;
  const canServiceNext = !!nextContinuationToken;

  return (
    <div className="dgl-page">
      <Toaster position="top-right" />

      <div className="dgl-toolbar">
        <div className="dgl-filters">
          <div className="dgl-field">
            <label>Event ID</label>
            <input
              className="dgl-input dgl-input-sm"
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              placeholder="Event ID"
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
          </div>

          <div className="dgl-field dgl-date">
            <label>Date Range</label>
            <DateRangePicker
              value={dateRange}
              onChange={(value) => {
                if (!value || value.length !== 2) {
                  setDateRange(null);
                  return;
                }
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const min = subDays(today, 90);
                const from = new Date(value[0]);
                const to = new Date(value[1]);
                from.setHours(0, 0, 0, 0);
                to.setHours(0, 0, 0, 0);
                if (from < min || to > today || daysBetween(new Date(from), new Date(to)) > 90) {
                  toast.error("Only the last 90 days can be selected");
                  return;
                }
                setDateRange(value);
              }}
              onOk={() => handleSearch()}
              ranges={predefinedBottomRanges.filter((r) => r.label !== "Last month")}
              placeholder="Select range"
              cleanable
              format="MM/dd/yyyy"
              calendarDefaultDate={new Date()}
              disabledDate={(date) => {
                const d = new Date(date);
                d.setHours(0, 0, 0, 0);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return d < subDays(today, 90) || d > today;
              }}
              shouldDisableDate={(date) => {
                const d = new Date(date);
                d.setHours(0, 0, 0, 0);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return d < subDays(today, 90) || d > today;
              }}
            />
          </div>

          <div className="dgl-field">
            <label>App Name</label>
            <input
              className="dgl-input dgl-input-md"
              value={applicationName}
              onChange={(e) => setApplicationName(e.target.value)}
              placeholder="Optional"
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
          </div>

          <div className="dgl-switch">
            <span className={tab === "service" ? "on" : ""}>SERVICE</span>
            <button
              type="button"
              className={`dgl-slider ${tab === "api" ? "right" : ""}`}
              onClick={() => switchTab(tab === "service" ? "api" : "service")}
            >
              <span className="dgl-knob" />
            </button>
            <span className={tab === "api" ? "on" : ""}>API</span>
          </div>

          <div className="dgl-actions">
            <button type="button" className="dgl-btn dgl-btn-search" onClick={handleSearch} disabled={loading}>
              <Search size={14} />
              {loading ? "Loading..." : "Search"}
            </button>
            <button type="button" className="dgl-btn dgl-btn-reset" onClick={handleReset} disabled={loading}>
              <RefreshCw size={14} />
              Reset
            </button>
            <button type="button" className="dgl-btn dgl-btn-reset" onClick={handleDownload} disabled={loading || !rows.length}>
              <Download size={14} />
              Excel
            </button>
          </div>
        </div>
      </div>

      <div className="dgl-table-wrap">
        <table className="dgl-table">
          <thead>
            {tab === "service" ? (
              <tr>
                <th>PartitionKey</th>
                <th>Event ID</th>
                <th>RowKey</th>
                <th>Timestamp</th>
                <th>AggregatedProperties</th>
                <th>ApplicationName</th>
                <th>Level</th>
                <th>MachineName</th>
                <th>RenderedMessage</th>
                <th>Exception</th>
              </tr>
            ) : (
              <tr>
                <th>PartitionKey</th>
                <th>Event ID</th>
                <th>RowKey</th>
                <th>Timestamp</th>
                <th>AggregatedProperties</th>
                <th>ApplicationName</th>
                <th>IpAddress</th>
                <th>Level</th>
                <th>MachineName</th>
                <th>RenderedMessage</th>
                <th>Exception</th>
              </tr>
            )}
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={tab === "service" ? 10 : 11} className="dgl-empty">
                  {loading ? "Loading..." : "Enter Event ID or Date Range and click Search"}
                </td>
              </tr>
            ) : tab === "service" ? (
              pageRows.map((r, i) => (
                <tr key={`${r.partitionKey}|${r.rowKey}|${i}`}>
                  <ClampCell title="PartitionKey" text={r.partitionKey} onOpen={openPopup} />
                  <ClampCell title="Event ID" text={r.eventId} onOpen={openPopup} />
                  <ClampCell title="RowKey" text={r.rowKey} onOpen={openPopup} />
                  <td>{formatTs(r.timestamp)}</td>
                  <ClampCell title="AggregatedProperties" text={r.aggregatedProperties} onOpen={openPopup} />
                  <ClampCell title="ApplicationName" text={r.applicationName} onOpen={openPopup} />
                  <ClampCell title="Level" text={r.level} onOpen={openPopup} />
                  <ClampCell title="MachineName" text={r.machineName} onOpen={openPopup} />
                  <ClampCell title="RenderedMessage" text={r.renderedMessage} onOpen={openPopup} />
                  <ClampCell title="Exception" text={r.exception} onOpen={openPopup} />
                </tr>
              ))
            ) : (
              pageRows.map((r, i) => (
                <tr key={`${r.partitionKey}|${r.rowKey}|${i}`}>
                  <ClampCell title="PartitionKey" text={r.partitionKey} onOpen={openPopup} />
                  <ClampCell title="Event ID" text={r.eventId} onOpen={openPopup} />
                  <ClampCell title="RowKey" text={r.rowKey} onOpen={openPopup} />
                  <td>{formatTs(r.timestamp)}</td>
                  <ClampCell title="AggregatedProperties" text={r.aggregatedProperties} onOpen={openPopup} />
                  <ClampCell title="ApplicationName" text={r.applicationName} onOpen={openPopup} />
                  <ClampCell title="IpAddress" text={r.ipAddress} onOpen={openPopup} />
                  <ClampCell title="Level" text={r.level} onOpen={openPopup} />
                  <ClampCell title="MachineName" text={r.machineName} onOpen={openPopup} />
                  <ClampCell title="RenderedMessage" text={r.renderedMessage} onOpen={openPopup} />
                  <ClampCell title="Exception" text={r.exception} onOpen={openPopup} />
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {rows.length > 0 && (
        <div className="dgl-pager">
          <Select
            options={pageSizeOptions}
            value={pageSizeOptions.find((o) => o.value === pageSize)}
            onChange={handlePageSizeChange}
            styles={selectStyles}
            menuPlacement="top"
            isSearchable={false}
          />
          {tab === "service" ? (
            <span>Page {pageNumber}</span>
          ) : (
            <span>
              {(safePage - 1) * pageSize + 1}-{Math.min(safePage * pageSize, totalCount)} of {totalCount}
            </span>
          )}
          <button
            type="button"
            className="dgl-btn dgl-btn-reset"
            disabled={tab === "service" ? !canServicePrev || loading : safePage <= 1}
            onClick={tab === "service" ? handleServicePrev : () => setPageNumber(safePage - 1)}
          >
            Prev
          </button>
          <button
            type="button"
            className="dgl-btn dgl-btn-reset"
            disabled={tab === "service" ? !canServiceNext || loading : safePage >= totalPages}
            onClick={tab === "service" ? handleServiceNext : () => setPageNumber(safePage + 1)}
          >
            Next
          </button>
        </div>
      )}

      {popup && (
        <div className="dgl-overlay" onClick={() => setPopup(null)}>
          <div className="dgl-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dgl-modal-header">
              <h3>{popup.title}</h3>
              <button type="button" className="dgl-close" onClick={() => setPopup(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="dgl-modal-body">
              <pre>{popup.text}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentGenerationLogsReport;